import React from "react";
import { UseFormRegister, FieldErrors } from "react-hook-form";
import { Ship } from "lucide-react";
import { FormField, Input, Select } from "../UI/FormField";
import { SectionCard } from "../UI/SectionCard";
import { CreateShipmentData } from "../../types";
import { CONTAINER_TYPE_OPTIONS } from "../../constants/shipmentFormConstants";
import { useTranslation } from "react-i18next";

interface SeaDetailsSectionProps {
  register: UseFormRegister<CreateShipmentData>;
  errors: FieldErrors<CreateShipmentData>;
  readonly: boolean;
  getErrorMessage: (error: any) => string | undefined;
}

// Sea freight (vessel + carrier) details — required when transportMode = SEA.
// The B/L document fields (HBL no/date, originals, B/L type, freight terms,
// places, notify party) live in the separate Bill of Lading accordion. The
// forwarder sub-block is always optional.
export const SeaDetailsSection: React.FC<SeaDetailsSectionProps> = ({
  register,
  errors,
  readonly,
  getErrorMessage,
}) => {
  const { t } = useTranslation("shipments");
  const seaErrors = errors.seaDetails;
  const forwarderErrors = seaErrors?.forwarder;

  return (
    <SectionCard icon={Ship} title={t("sea.title")}>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-6 gap-y-1">
        <div className="space-y-1">
          {/* Container number — relocated here from the Transport block for SEA.
              Still stored on carrier.containerNumber. */}
          <FormField
            layout="inline"
            size="sm"
            label={t("sea.containerNumber")}
            error={getErrorMessage(errors.carrier?.containerNumber)}
          >
            <Input
              {...register("carrier.containerNumber")}
              error={!!errors.carrier?.containerNumber}
              placeholder={t("sea.containerNumberPlaceholder")}
              readOnly={readonly}
              size="sm"
            />
          </FormField>

          <FormField
            layout="inline"
            size="sm"
            label={t("sea.shippingLine")}
            error={getErrorMessage(seaErrors?.shippingLine)}
          >
            <Input
              {...register("seaDetails.shippingLine")}
              error={!!seaErrors?.shippingLine}
              placeholder={t("sea.shippingLinePlaceholder")}
              readOnly={readonly}
              size="sm"
            />
          </FormField>

          <FormField
            layout="inline"
            size="sm"
            label={t("sea.vesselName")}
            error={getErrorMessage(seaErrors?.vesselName)}
          >
            <Input
              {...register("seaDetails.vesselName")}
              error={!!seaErrors?.vesselName}
              placeholder={t("sea.vesselNamePlaceholder")}
              readOnly={readonly}
              size="sm"
            />
          </FormField>

          <FormField
            layout="inline"
            size="sm"
            label={t("sea.voyageNumber")}
            error={getErrorMessage(seaErrors?.voyageNumber)}
          >
            <Input
              {...register("seaDetails.voyageNumber")}
              error={!!seaErrors?.voyageNumber}
              placeholder={t("sea.voyageNumberPlaceholder")}
              readOnly={readonly}
              size="sm"
            />
          </FormField>

          <FormField
            layout="inline"
            size="sm"
            label={t("sea.bookingNumber")}
            error={getErrorMessage(seaErrors?.bookingNumber)}
          >
            <Input
              {...register("seaDetails.bookingNumber")}
              error={!!seaErrors?.bookingNumber}
              placeholder={t("sea.bookingNumberPlaceholder")}
              readOnly={readonly}
              size="sm"
            />
          </FormField>

          <FormField
            layout="inline"
            size="sm"
            label={t("sea.portOfLoading")}
            error={getErrorMessage(seaErrors?.portOfLoading)}
          >
            <Input
              {...register("seaDetails.portOfLoading")}
              error={!!seaErrors?.portOfLoading}
              placeholder={t("sea.portOfLoadingPlaceholder")}
              readOnly={readonly}
              size="sm"
            />
          </FormField>

          <FormField
            layout="inline"
            size="sm"
            label={t("sea.masterBLNumber")}
            error={getErrorMessage(seaErrors?.masterBLNumber)}
          >
            <Input
              {...register("seaDetails.masterBLNumber")}
              error={!!seaErrors?.masterBLNumber}
              placeholder={t("sea.masterBLNumberPlaceholder")}
              readOnly={readonly}
              size="sm"
            />
          </FormField>
        </div>

        <div className="space-y-1">
          <FormField
            layout="inline"
            size="sm"
            label={t("sea.containerType")}
            error={getErrorMessage(seaErrors?.containerType)}
          >
            <Select
              {...register("seaDetails.containerType")}
              options={CONTAINER_TYPE_OPTIONS}
              error={!!seaErrors?.containerType}
              disabled={readonly}
              placeholder={t("sea.containerTypePlaceholder")}
              size="sm"
            />
          </FormField>

          <FormField
            layout="inline"
            size="sm"
            label={t("sea.portOfDischarge")}
            error={getErrorMessage(seaErrors?.portOfDischarge)}
          >
            <Input
              {...register("seaDetails.portOfDischarge")}
              error={!!seaErrors?.portOfDischarge}
              placeholder={t("sea.portOfDischargePlaceholder")}
              readOnly={readonly}
              size="sm"
            />
          </FormField>

          <FormField
            layout="inline"
            size="sm"
            label={t("sea.sealNumber")}
            error={getErrorMessage(seaErrors?.sealNumber)}
          >
            <Input
              {...register("seaDetails.sealNumber")}
              error={!!seaErrors?.sealNumber}
              placeholder={t("sea.sealNumberPlaceholder")}
              readOnly={readonly}
              size="sm"
            />
          </FormField>

          <FormField
            layout="inline"
            size="sm"
            label={t("sea.imoNumber")}
            error={getErrorMessage(seaErrors?.imoNumber)}
          >
            <Input
              {...register("seaDetails.imoNumber")}
              error={!!seaErrors?.imoNumber}
              placeholder={t("sea.imoNumberPlaceholder")}
              readOnly={readonly}
              size="sm"
            />
          </FormField>

          <FormField
            layout="inline"
            size="sm"
            label={t("sea.etd")}
            error={getErrorMessage(seaErrors?.etd)}
          >
            <Input
              {...register("seaDetails.etd")}
              type="date"
              error={!!seaErrors?.etd}
              readOnly={readonly}
              size="sm"
            />
          </FormField>

          <FormField
            layout="inline"
            size="sm"
            label={t("sea.eta")}
            error={getErrorMessage(seaErrors?.eta)}
          >
            <Input
              {...register("seaDetails.eta")}
              type="date"
              error={!!seaErrors?.eta}
              readOnly={readonly}
              size="sm"
            />
          </FormField>
        </div>
      </div>

      {/* Forwarder — optional */}
      <div className="mt-3 pt-3 border-t border-gray-100">
        <h4 className="text-[12px] font-semibold text-gray-500 uppercase tracking-wide mb-2">
          {t("forwarder.title")}
        </h4>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-6 gap-y-1">
          <div className="space-y-1">
            <FormField
              layout="inline"
              size="sm"
              label={t("forwarder.companyName")}
              error={getErrorMessage(forwarderErrors?.companyName)}
            >
              <Input
                {...register("seaDetails.forwarder.companyName")}
                error={!!forwarderErrors?.companyName}
                placeholder={t("forwarder.companyNamePlaceholder")}
                readOnly={readonly}
                size="sm"
              />
            </FormField>

            <FormField
              layout="inline"
              size="sm"
              label={t("forwarder.contactPerson")}
              error={getErrorMessage(forwarderErrors?.contactPerson)}
            >
              <Input
                {...register("seaDetails.forwarder.contactPerson")}
                error={!!forwarderErrors?.contactPerson}
                placeholder={t("forwarder.contactPersonPlaceholder")}
                readOnly={readonly}
                size="sm"
              />
            </FormField>

            <FormField
              layout="inline"
              size="sm"
              label={t("labels.email")}
              error={getErrorMessage(forwarderErrors?.email)}
            >
              <Input
                {...register("seaDetails.forwarder.email")}
                type="email"
                error={!!forwarderErrors?.email}
                placeholder={t("placeholders.email")}
                readOnly={readonly}
                size="sm"
              />
            </FormField>
          </div>

          <div className="space-y-1">
            <FormField
              layout="inline"
              size="sm"
              label={t("labels.phone")}
              error={getErrorMessage(forwarderErrors?.phone)}
            >
              <Input
                {...register("seaDetails.forwarder.phone")}
                error={!!forwarderErrors?.phone}
                placeholder={t("placeholders.phone")}
                readOnly={readonly}
                size="sm"
              />
            </FormField>

            <FormField
              layout="inline"
              size="sm"
              label={t("forwarder.houseBLNumber")}
              error={getErrorMessage(forwarderErrors?.houseBLNumber)}
            >
              <Input
                {...register("seaDetails.forwarder.houseBLNumber")}
                error={!!forwarderErrors?.houseBLNumber}
                placeholder={t("forwarder.houseBLNumberPlaceholder")}
                readOnly={readonly}
                size="sm"
              />
            </FormField>

            <FormField
              layout="inline"
              size="sm"
              label={t("forwarder.reference")}
              error={getErrorMessage(forwarderErrors?.reference)}
            >
              <Input
                {...register("seaDetails.forwarder.reference")}
                error={!!forwarderErrors?.reference}
                placeholder={t("forwarder.referencePlaceholder")}
                readOnly={readonly}
                size="sm"
              />
            </FormField>
          </div>
        </div>
      </div>
    </SectionCard>
  );
};

SeaDetailsSection.displayName = "SeaDetailsSection";
